import type { OfferStatus } from '@neon-one/auctions-types';

/**
 * What the bidder is shown about an offer.
 *
 * Deliberately not the same set as the wire's `OfferStatus`: the wire's
 * `cancelled` is an admin act ("I pulled the lot back"), and to the bidder
 * that reads as **withdrawn** — a state of its own, never folded into
 * `declined` (which is her own decision) or `expired` (which is the clock).
 */
export const OFFER_VIEW_STATE = {
  pending: 'pending',
  accepted: 'accepted',
  declined: 'declined',
  expired: 'expired',
  withdrawn: 'withdrawn',
} as const;

export type OfferViewState = (typeof OFFER_VIEW_STATE)[keyof typeof OFFER_VIEW_STATE];

/** Wire status → what the bidder is told. Only `pending` consults the clock. */
const WIRE_TO_VIEW: Record<Exclude<OfferStatus, 'pending'>, OfferViewState> = {
  accepted: OFFER_VIEW_STATE.accepted,
  declined: OFFER_VIEW_STATE.declined,
  expired: OFFER_VIEW_STATE.expired,
  cancelled: OFFER_VIEW_STATE.withdrawn,
};

/** Message severity + icon per terminal state, so the page never picks its own. */
export const OFFER_VIEW_TONE: Record<OfferViewState, { severity: string; icon: string }> = {
  [OFFER_VIEW_STATE.pending]: { severity: 'info', icon: 'pi pi-tag' },
  [OFFER_VIEW_STATE.accepted]: { severity: 'success', icon: 'pi pi-check-circle' },
  [OFFER_VIEW_STATE.declined]: { severity: 'secondary', icon: 'pi pi-check-circle' },
  [OFFER_VIEW_STATE.expired]: { severity: 'secondary', icon: 'pi pi-clock' },
  [OFFER_VIEW_STATE.withdrawn]: { severity: 'secondary', icon: 'pi pi-undo' },
};

interface OfferViewOptions {
  /** Injectable clock so the lapse boundary is testable. */
  now?: number;
  /** The decline POST succeeded but the store still holds the old offer. */
  locallyDeclined?: boolean;
}

/**
 * The only two facts this module reads off an offer.
 *
 * Structural on purpose: the offer page holds a full `OfferDetail` from
 * `GET /offers/:uuid`, while My Wins holds a `BidderOutstandingOffer` summary
 * from `/me/bids`. Both must answer "what is this offer" the same way, and the
 * answer is one function — a second copy of the precedence rule on the wins
 * side is exactly how the two surfaces start disagreeing about a withdrawn
 * offer.
 */
export interface OfferLifecycleFacts {
  status: OfferStatus;
  expiresAt: string | null;
}

/**
 * Resolve what to show for an offer.
 *
 * Precedence is the whole point: a status the server has already decided
 * always beats the deadline. A cancelled offer that is *also* past its
 * expiry is withdrawn, not expired — the admin's action is what ended it,
 * and telling her it "expired" would be a small lie about who did what.
 */
export function offerViewState(
  offer: OfferLifecycleFacts | null | undefined,
  options: OfferViewOptions = {},
): OfferViewState | null {
  if (!offer) return null;

  if (offer.status !== 'pending') return WIRE_TO_VIEW[offer.status];
  if (options.locallyDeclined) return OFFER_VIEW_STATE.declined;

  const now = options.now ?? Date.now();
  const expiresAt = offer.expiresAt ? Date.parse(offer.expiresAt) : NaN;
  if (!Number.isNaN(expiresAt) && expiresAt <= now) return OFFER_VIEW_STATE.expired;

  return OFFER_VIEW_STATE.pending;
}

/** Minutes until the deadline; null when there is no usable deadline. */
export function minutesUntil(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((at - now) / 60_000));
}

/**
 * Inside this window the deadline stops being a date and becomes a countdown.
 * One threshold for every surface that shows the offer, so the wins callout
 * and the offer page never disagree about when it turns urgent.
 */
export const EXPIRING_SOON_MINUTES = 60;

/** True while the offer is live but close enough to the deadline to press. */
export function offerExpiringSoon(iso: string | null | undefined, now = Date.now()): boolean {
  const minutes = minutesUntil(iso, now);
  return minutes != null && minutes > 0 && minutes < EXPIRING_SOON_MINUTES;
}
